import { ItemsPerPage } from 'constants/index';
import React, { useContext } from 'react';
import { TabNames } from 'types/enums';
import FiltersProvider from 'components/Filters/context/FiltersContext/FiltersContext';
import { neverop } from 'utils';
import { SearchContextType } from './types';
import useSearchResults from './hooks/useSearchResults';
import useSearchFiltersMeta from './hooks/useSearchFiltersMeta';
import { useSearchAllFiltersMeta } from './hooks/useSearchAllFiltersMeta';

const DefaultResults = {
    packs: [],
    packsError: undefined,
    packsLoading: false,
    packsTotal: undefined,
    packsFilterMetaObj: undefined,
    packsCompleted: undefined,
    updatePacksLoading: neverop('SearchProvider', 'updatePacksLoading'),
    updatePacksError: neverop('SearchProvider', 'updatePacksError'),
    updatePacksResult: neverop('SearchProvider', 'updatePacksResult'),
    updatePacksCompleted: neverop('SearchProvider', 'updatePacksCompleted'),
    samples: [],
    samplesError: undefined,
    samplesLoading: false,
    samplesTotal: undefined,
    samplesFilterMetaObj: undefined,
    samplesCompleted: undefined,
    updateSamplesResult: neverop('SearchProvider', 'updateSamplesResult'),
    updateSamplesLoading: neverop('SearchProvider', 'updateSamplesLoading'),
    updateSamplesError: neverop('SearchProvider', 'updateSamplesError'),
    updateSamplesCompleted: neverop('SearchProvider', 'updateSamplesCompleted'),
    activeTabTotal: undefined,
    filterMetaObj: undefined,
};

const DefaultFilterMeta = {
    filterGenresMeta: undefined,
    filterInstrumentsMeta: undefined,
    filterTypesMeta: undefined,
    filterSfxsMeta: undefined,
    filterTagsMeta: undefined,
};

const DefaultSearchAllFiltersMeta = {
    allSamplesTotal: undefined,
    samplesAllFiltersMeta: DefaultFilterMeta,
    updateAllSamplesResult: neverop('SearchProvider', 'updateAllSamplesResult'),
    updateAllSamplesLoading: neverop(
        'SearchProvider',
        'updateAllSamplesLoading',
    ),
    updateAllSamplesError: neverop('SearchProvider', 'updateAllSamplesError'),
    updateAllSamplesCompleted: neverop(
        'SearchProvider',
        'updateAllSamplesCompleted',
    ),
    allPacksTotal: undefined,
    packsAllFiltersMeta: DefaultFilterMeta,
    updateAllPacksResult: neverop('SearchProvider', 'updateAllPacksResult'),
    updateAllPacksLoading: neverop('SearchProvider', 'updateAllPacksLoading'),
    updateAllPacksError: neverop('SearchProvider', 'updateAllPacksError'),
    updateAllPacksCompleted: neverop(
        'SearchProvider',
        'updateAllPacksCompleted',
    ),
    filtersLoading: false,
};

export const DefaultSearchContext: SearchContextType = {
    ...DefaultResults,
    ...DefaultFilterMeta,
    ...DefaultSearchAllFiltersMeta,
    itemsPerPage: 24,
    baseUrl: undefined,
    isReady: false,
    defaultTab: TabNames.Samples,
};

export const SearchContext =
    React.createContext<SearchContextType>(DefaultSearchContext);

export const useSearchController = (): SearchContextType =>
    useContext(SearchContext);

export const SearchProvider = ({
    itemsPerPage = ItemsPerPage,
    children,
    defaultTab = TabNames.Samples,
    baseUrl,
}: {
    itemsPerPage?: number;
    children: React.ReactNode;
    defaultTab?: TabNames;
    baseUrl?: string;
}): React.ReactElement => {
    const results = useSearchResults({
        defaultTab,
    });

    const filtersMeta = useSearchFiltersMeta({
        filterMetaObj: results.filterMetaObj,
    });

    const searchAllFiltersMeta = useSearchAllFiltersMeta();

    const { samplesCompleted, packsCompleted } = results;
    const { allSamplesCompleted, allPacksCompleted } = searchAllFiltersMeta;
    const completedStatus = [
        samplesCompleted,
        packsCompleted,
        allSamplesCompleted,
        allPacksCompleted,
    ];
    const isOneIncomplete = completedStatus.some((status) => status === false);
    const areAllUndefined = completedStatus.every(
        (status) => status === undefined,
    );

    const isReady = !isOneIncomplete && !areAllUndefined;

    const context = {
        ...results,
        ...filtersMeta,
        ...searchAllFiltersMeta,
        itemsPerPage,
        baseUrl,
        isReady,
        defaultTab,
    };

    return (
        <SearchContext.Provider value={context}>
            <FiltersProvider>{children}</FiltersProvider>
        </SearchContext.Provider>
    );
};

export default SearchProvider;
